// Decoding and encoding of standard Bluetooth (GATT) frames: pure functions, no browser, tested byte by byte.
// References: Fitness Machine Service 1.0, Cycling Power Service 1.1, Cycling Speed and Cadence 1.0, Heart Rate 1.0.

export const UUID = {
  ftms: 0x1826, indoorBikeData: 0x2ad2, ftmsControlPoint: 0x2ad9, ftmsFeature: 0x2acc, powerRange: 0x2ad8, resistanceRange: 0x2ad6,
  cps: 0x1818, cpsMeasurement: 0x2a63,
  csc: 0x1816, cscMeasurement: 0x2a5b,
} as const;

/** Useful frame fields: power (W), cadence (rpm), speed (km/h), heart rate (bpm). */
export interface Reading { power?: number; cadence?: number; speed?: number; hr?: number }

/**
 * Rate of a cumulative, timestamped revolution counter (wheel or crank).
 * Sensors keep returning the same total while nothing turns: after `stopAfter` ms without a new revolution, the rate is 0.
 */
export class RevRate {
  private last: { revs: number; ticks: number; at: number } | null = null;
  /**
   * @param ticksPerSec timestamp resolution (1024 or 2048)
   * @param revMod      revolution counter modulo (2^16 or 2^32)
   * @param maxRate     maximum plausible rate (rev/s); above it the sensor was reset: resynchronise
   */
  constructor(private readonly ticksPerSec: number, private readonly revMod: number, private readonly maxRate: number, private readonly stopAfter = 3000) {}

  /** Revolutions per second, or null if the frame brings no new measurement. */
  update(revs: number, ticks: number, now: number): number | null {
    const L = this.last;
    if (!L) { this.last = { revs, ticks, at: now }; return null; }
    const dr = (revs - L.revs + this.revMod) % this.revMod;
    const dt = ((ticks - L.ticks + 65536) % 65536) / this.ticksPerSec;
    if (dr === 0 || dt === 0) return now - L.at > this.stopAfter ? 0 : null;
    this.last = { revs, ticks, at: now };
    const rate = dr / dt;
    return rate > this.maxRate ? null : rate;
  }
  reset() { this.last = null; }
}

const wheelRate = (ticksPerSec: number) => new RevRate(ticksPerSec, 2 ** 32, 40); // 40 rev/s ≈ 300 km/h: above that, error
const crankRate = () => new RevRate(1024, 65536, 4); // 4 rev/s = 240 rpm
const kmh = (revsPerSec: number, wheelMm: number) => (revsPerSec * wheelMm * 3.6) / 1000;

/** Indoor Bike Data (FTMS 0x2AD2). */
export function parseIndoorBikeData(v: DataView): Reading {
  const fl = v.getUint16(0, true), d: Reading = {};
  let o = 2;
  if (!(fl & 0x01)) { d.speed = v.getUint16(o, true) / 100; o += 2; } // bit 0 "more data": speed is absent when it is 1
  if (fl & 0x02) o += 2; // average speed
  if (fl & 0x04) { d.cadence = v.getUint16(o, true) / 2; o += 2; }
  if (fl & 0x08) o += 2; // average cadence
  if (fl & 0x10) o += 3; // distance
  if (fl & 0x20) o += 2; // resistance level
  if (fl & 0x40) { d.power = v.getInt16(o, true); o += 2; }
  if (fl & 0x80) o += 2; // average power
  if (fl & 0x100) o += 5; // energy
  if (fl & 0x200 && o < v.byteLength) d.hr = v.getUint8(o);
  return d;
}

/** Cycling Power Measurement (0x2A63): power, plus cadence and speed if the sensor sends its counters. */
export class CyclingPowerParser {
  private crank = crankRate();
  private wheel = wheelRate(2048);
  constructor(public wheelMm: number) {}
  parse(v: DataView, now: number): Reading {
    const fl = v.getUint16(0, true), d: Reading = { power: v.getInt16(2, true) };
    let o = 4;
    if (fl & 0x01) o += 1; // left/right balance
    if (fl & 0x04) o += 2; // accumulated torque
    if (fl & 0x10) {
      const r = this.wheel.update(v.getUint32(o, true), v.getUint16(o + 4, true), now);
      if (r != null) d.speed = kmh(r, this.wheelMm);
      o += 6;
    }
    if (fl & 0x20) {
      const r = this.crank.update(v.getUint16(o, true), v.getUint16(o + 2, true), now);
      if (r != null) d.cadence = r * 60;
    }
    return d;
  }
  reset() { this.crank.reset(); this.wheel.reset(); }
}

/** CSC Measurement (0x2A5B): speed (wheel) and/or cadence (crank) sensor. */
export class SpeedCadenceParser {
  private crank = crankRate();
  private wheel = wheelRate(1024);
  constructor(public wheelMm: number) {}
  parse(v: DataView, now: number): Reading {
    const fl = v.getUint8(0), d: Reading = {};
    let o = 1;
    if (fl & 0x01) {
      const r = this.wheel.update(v.getUint32(o, true), v.getUint16(o + 4, true), now);
      if (r != null) d.speed = kmh(r, this.wheelMm);
      o += 6;
    }
    if (fl & 0x02) {
      const r = this.crank.update(v.getUint16(o, true), v.getUint16(o + 2, true), now);
      if (r != null) d.cadence = r * 60;
    }
    return d;
  }
  reset() { this.crank.reset(); this.wheel.reset(); }
}

/** Heart Rate Measurement (0x2A37). */
export const parseHeartRate = (v: DataView): number => (v.getUint8(0) & 1 ? v.getUint16(1, true) : v.getUint8(1));

/** What the trainer agrees to control (Fitness Machine Feature, 0x2ACC). */
export interface FtmsCaps { sim: boolean; erg: boolean; resistance: boolean }
/** Without a readable characteristic, assume everything is available: this is how most recent trainers behave. */
export const ALL_CAPS: FtmsCaps = { sim: true, erg: true, resistance: true };
export function parseFtmsFeature(v: DataView): FtmsCaps {
  if (v.byteLength < 8) return { ...ALL_CAPS };
  const target = v.getUint32(4, true);
  return { resistance: !!(target & (1 << 2)), erg: !!(target & (1 << 3)), sim: !!(target & (1 << 13)) };
}

/** Advertised range (power in W, or resistance level in tenths). */
export interface Range { min: number; max: number }
export function parseRange(v: DataView): Range | null {
  if (v.byteLength < 4) return null;
  const min = v.getInt16(0, true), max = v.getInt16(2, true);
  return max > min ? { min, max } : null;
}

// ---- FTMS control point (0x2AD9) ----
export const FTMS_OP = { requestControl: 0x00, reset: 0x01, resistance: 0x04, power: 0x05, start: 0x07, stop: 0x08, sim: 0x11, response: 0x80 } as const;

/** Simulation: wind (m/s, resolution 0.001), grade (%, 0.01), rolling resistance (0.0001), drag (kg/m, 0.01 → 0.51). */
export function encodeSim(wind: number, grade: number, crr: number): Uint8Array {
  const b = new Uint8Array(7), dv = new DataView(b.buffer);
  b[0] = FTMS_OP.sim;
  dv.setInt16(1, clampInt(wind * 1000, -32768, 32767), true);
  dv.setInt16(3, clampInt(grade * 100, -32768, 32767), true);
  b[5] = clampInt(crr * 10000, 0, 255);
  b[6] = 51;
  return b;
}
/** ERG: target power (W), clamped to the range advertised by the trainer. */
export function encodeErg(watts: number, range: Range | null): Uint8Array {
  const lo = range ? Math.max(0, range.min) : 0, hi = range ? Math.min(2500, range.max) : 2500;
  const b = new Uint8Array(3);
  b[0] = FTMS_OP.power;
  new DataView(b.buffer).setInt16(1, clampInt(watts, lo, hi), true);
  return b;
}
/** Resistance level: 0–1 fraction of the advertised range (UINT8 parameter in tenths, FTMS 1.0). */
export function encodeResistance(frac: number, range: Range | null): Uint8Array {
  const r = range ?? { min: 0, max: 100 }, lo = Math.max(0, r.min), hi = Math.min(255, r.max);
  return new Uint8Array([FTMS_OP.resistance, clampInt(lo + (hi - lo) * Math.max(0, Math.min(1, frac)), lo, hi)]);
}

const clampInt = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, Math.round(Number.isFinite(x) ? x : 0)));
